import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  IoArrowBack, 
  IoLocationOutline, 
  IoFlashOutline, 
  IoTimeOutline,
  IoChatbubbleEllipsesOutline,
  IoHeartOutline
} from 'react-icons/io5';
import { useLanguage } from './contexts/LanguageContext';
import AppPromoCard from './components/AppPromoCard';
import AppPopup from './components/AppPopup';
import { incrementSessionViewCount } from './utils/promoTracking';
import './StationDetail.css';

function StationDetail() {
  const { stationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [station, setStation] = useState(location.state?.station || null);
  
  const [combinedHistory, setCombinedHistory] = useState([]);
  const [currentAvailability, setCurrentAvailability] = useState({ available: 0, total: 0 });
  const [portDetails, setPortDetails] = useState([]); // Port charging speed data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intervalDuration, setIntervalDuration] = useState(10); // Default to 10 minutes
  const [start_time, setStartTime] = useState(null);
  
  // Intelligence data state
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceError, setIntelligenceError] = useState(null);
  
  // UI state for collapsible sections
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showPortHistory, setShowPortHistory] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  
  // Modified function to ensure consistent bar lengths
  const combineIntervals = (minuteStatus) => {
    if (!minuteStatus || minuteStatus.length === 0) return [];
    
    const combinedIntervals = [];
    let currentStatus = minuteStatus[0];
    let startInterval = 0;
    
    for (let i = 1; i <= minuteStatus.length; i++) {
      // If status changes or we've reached the end
      if (i === minuteStatus.length || minuteStatus[i] !== currentStatus) {
        combinedIntervals.push({
          status: currentStatus,
          startInterval: startInterval,
          endInterval: i - 1,
          width: ((i - startInterval) / minuteStatus.length) * 100 // Width as percentage
        });
        
        if (i < minuteStatus.length) {
          currentStatus = minuteStatus[i];
          startInterval = i;
        }
      }
    }
    
    // Verify total width adds up to 100%
    const totalWidth = combinedIntervals.reduce((sum, interval) => sum + interval.width, 0);
    
    // If total width is not exactly 100%, normalize the widths
    if (Math.abs(totalWidth - 100) > 0.01) {
      const normalizationFactor = 100 / totalWidth;
      combinedIntervals.forEach(interval => {
        interval.width = interval.width * normalizationFactor;
      });
      
      console.log(`Normalized widths for a port (original total: ${totalWidth.toFixed(2)}%)`);
    }
    
    return combinedIntervals;
  };
  
  // Fetch station data if not provided in location state
  useEffect(() => {
    if (!station && stationId) {
      const fetchStation = async () => {
        try {
          console.log(`Fetching station details for ID: ${stationId}`);
          const response = await fetch(`/api/stations/${stationId}`);
          
          console.log('Station details response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Failed to fetch station: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('Station details data:', data);
          setStation(data);
        } catch (err) {
          console.error('Error fetching station:', err);
          setError('Failed to load station information. Please try again later.');
        }
      };
      
      fetchStation();
    }
  }, [station, stationId]);
  
  // Track station view count for popup trigger
  useEffect(() => {
    incrementSessionViewCount();
  }, []);

  // Fetch availability and history data
  useEffect(() => {
    if (station) {
      setIsLoading(true);
      setError(null);
      
      // Fetch current availability
      const fetchAvailability = async () => {
        try {
          console.log(`Fetching availability for station ID: ${station.id}`);
          const response = await fetch(`/api/stations/${station.id}/availability`);
          
          console.log('Availability response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Failed to fetch availability: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('Availability data:', data);
          setCurrentAvailability({
            available: data.availablePorts || 0,
            total: data.totalPorts || station.totalPorts
          });
        } catch (err) {
          console.error('Error fetching availability:', err);
          // Fallback to station data
          setCurrentAvailability({
            available: 0,
            total: station.totalPorts
          });
        }
      };
      
      // Fetch 24-hour history data
      const fetchHistory = async () => {
        try {
          // Get current time and 24 hours ago
          const endTime = new Date();
          const startTime = new Date(endTime);
          startTime.setHours(startTime.getHours() - 24);
          
          // Save the start time for use in time labels
          setStartTime(startTime);
          
          // Format dates for API
          const formatDate = (date) => date.toISOString();
          
          console.log(`Fetching history for station ID: ${station.id}`);
          console.log(`Time range: ${formatDate(startTime)} to ${formatDate(endTime)}`);
          
          const response = await fetch(
            `/api/stations/${station.id}/history?startTime=${formatDate(startTime)}&endTime=${formatDate(endTime)}`
          );
          
          console.log('History response status:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Failed to fetch history: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('History data:', data);
          
          setIntervalDuration(data.intervalDuration || 10);
          
          // Find the maximum length of status arrays across all ports
          const maxStatusLength = Math.max(
            ...data.ports.map(port => port.status.length),
            0
          );
          
          // Process each port's data
          if (data.ports && Array.isArray(data.ports)) {
            const processedHistory = data.ports.map(port => {
              // Ensure all ports have the same length status array
              let statusArray = [...port.status];
              
              // If this port's status array is shorter than the maximum, pad with "not available" (0)
              if (statusArray.length < maxStatusLength) {
                console.log(`Port ${port.portNumber} has ${statusArray.length} status points, padding to ${maxStatusLength}`);
                statusArray = [
                  ...statusArray,
                  ...Array(maxStatusLength - statusArray.length).fill(0)
                ];
              }
              
              // Combine adjacent intervals with the same status
              const combinedIntervals = combineIntervals(statusArray);
              
              return {
                portNumber: port.portNumber,
                combinedIntervals,
                chargingSpeed: port.chargingSpeed,
                portType: port.portType,
                amperage: port.amperage,
                voltage: port.voltage,
                powerKw: port.powerKw
              };
            });
            
            setCombinedHistory(processedHistory);
          } else {
            console.error('Invalid history data format:', data);
            throw new Error('Invalid history data format received from server');
          }
          
          setIsLoading(false);
        } catch (err) {
          console.error('Error fetching history:', err);
          setError(`Failed to load availability history: ${err.message}`);
          setIsLoading(false);
        }
      };

      fetchAvailability();
      fetchHistory();
      fetchIntelligence();
    }
  }, [station]);
  
  // Fetch intelligence data
  const fetchIntelligence = async () => {
    if (!station) return;
    
    setIntelligenceLoading(true);
    setIntelligenceError(null);
    
    try {
      console.log(`Fetching intelligence for station ID: ${station.id}`);
      const response = await fetch(`/api/stations/${station.id}/intelligence`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch intelligence: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Intelligence data:', data);
      
      if (data.success) {
        setIntelligenceData(data.data);
      } else {
        throw new Error(data.error || 'Failed to load intelligence data');
      }
    } catch (err) {
      console.error('Error fetching intelligence:', err);
      setIntelligenceError(err.message);
    } finally {
      setIntelligenceLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 0: return 'var(--color-error)'; // Not available
      case 1: return 'var(--color-warning)'; // In use
      case 2: return 'var(--color-success)'; // Available
      default: return 'var(--color-gray-400)';
    }
  };
  
  const getStatusText = (status) => {
    switch(status) {
      case 0: return t('notAvailable');
      case 1: return t('inUse');
      case 2: return t('available');
      default: return 'Unknown';
    }
  };
  
  const formatTimeFromInterval = (interval) => {
    if (!start_time) return '';
    
    const totalMinutes = interval * intervalDuration;
    const date = new Date(start_time);
    date.setMinutes(date.getMinutes() + totalMinutes);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Intelligence helper functions
  const formatTimeBucket = (bucket) => {
    const hour = Math.floor(bucket);
    const minute = bucket % 1 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  };
  
  const getIntelligenceStatusColor = (status) => {
    switch(status) {
      case 'VACANT': return 'var(--color-success)';
      case 'LESS_BUSY': return 'var(--color-warning)';
      case 'BUSY': return 'var(--color-error)';
      default: return 'var(--color-gray-400)';
    }
  };
  
  const getIntelligenceStatusText = (status) => {
    switch(status) {
      case 'VACANT': return 'Usually Available';
      case 'LESS_BUSY': return t('shortWaits');
      case 'BUSY': return 'Expect Queues';
      default: return 'Unknown';
    }
  };
  
  const generateTimeLabels = () => {
    const labels = [];
    if (!start_time) return labels;
    
    const totalIntervals = combinedHistory.length > 0 && combinedHistory[0].combinedIntervals.length > 0 
      ? combinedHistory[0].combinedIntervals.reduce((sum, interval) => sum + (interval.endInterval - interval.startInterval + 1), 0)
      : 144; // Fallback to 24 hours with 10-minute intervals
    
    // Create labels for every 2 hours (12 intervals at 10 minutes each)
    const labelInterval = Math.max(1, Math.floor(totalIntervals / 12));
    
    for (let i = 0; i <= totalIntervals; i += labelInterval) {
      const position = (i / totalIntervals) * 100;
      labels.push({
        position: `${position}%`,
        time: formatTimeFromInterval(i)
      });
    }
    
    return labels;
  };

  // Mobile-specific functions for status visualization
  const getCurrentPortStatus = (port) => {
    if (!port.combinedIntervals || port.combinedIntervals.length === 0) return 0;
    // Get the most recent status (last interval)
    return port.combinedIntervals[port.combinedIntervals.length - 1].status;
  };

  const getCurrentSessionLength = (port) => {
    if (!port.combinedIntervals || port.combinedIntervals.length === 0) return 0;
    
    const currentStatus = getCurrentPortStatus(port);
    
    // Find the current ongoing session by working backwards from the end
    let sessionMinutes = 0;
    for (let i = port.combinedIntervals.length - 1; i >= 0; i--) {
      const interval = port.combinedIntervals[i];
      if (interval.status === currentStatus) { // Same status (available, in use, or not available)
        // Each interval represents intervalDuration minutes
        const intervalMinutes = (interval.endInterval - interval.startInterval + 1) * intervalDuration;
        sessionMinutes += intervalMinutes;
      } else {
        break; // Stop when we hit a different status
      }
    }
    
    return Math.round(sessionMinutes);
  };

  // Helper function to generate local time labels
  const generateMobileTimeLabels = () => {
    if (!start_time) return [];
    
    const labels = [];
    const now = new Date();
    
    // Generate labels for 24 hours, starting from 24 hours ago
    for (let i = 24; i >= 0; i--) {
      const time = new Date(now.getTime() - (i * 60 * 60 * 1000));
      if (i === 0) {
        labels.push('Now');
      } else {
        labels.push(time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      }
    }
    
    return labels;
  };

  // Helper function to group recommendations by type
  const getGroupedRecommendations = (recommendations) => {
    if (!recommendations) return {};
    
    const groups = {
      noWait: [],
      shortWait: [],
      expectWait: []
    };
    
    recommendations.forEach(rec => {
      if (rec.type === 'best_times') {
        if (rec.status === 'VACANT') {
          groups.noWait.push(rec);
        } else {
          groups.shortWait.push(rec);
        }
      } else {
        groups.expectWait.push(rec);
      }
    });
    
    return groups;
  };

  // Helper function to format time ranges for a group
  const formatTimeRanges = (recommendations) => {
    if (!recommendations || recommendations.length === 0) return '';
    
    return recommendations
      .map(rec => `${formatTimeBucket(rec.start_bucket)} - ${formatTimeBucket(rec.end_bucket)}`)
      .join(', ');
  };




  
  if (!station) {
    return (
      <div className="station-detail-container">
        <div className="loading-indicator">
          <IoFlashOutline size={48} />
          <p>{t('loadingStationInfo')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="station-detail-container">
      <div className="station-detail-header">
        <div className="header-content">
                      <button className="back-button" onClick={() => navigate('/', {
              state: { 
                selectedCounty: location.state?.selectedCounty,
                searchLocation: location.state?.searchLocation,
                nearbyStations: location.state?.nearbyStations
              }
            })}>
              <IoArrowBack size={16} />
              {t('back')}
            </button>
          <div className="station-title">
            <h1>{station.name}</h1>
            <p style={{ margin: '0 0 var(--space-2) 0', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>
              Electrify America Station
            </p>
            <div className="station-address">
              <IoLocationOutline size={16} />
              <span>{station.address}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="station-detail-content">
        
        {/* Port Status History Section - Primary */}
        <div className="history-section">
          <div className="intelligence-header">
            <IoTimeOutline size={24} color="var(--color-primary)" />
            <h3>Port Status</h3>
          </div>
          
          {isLoading ? (
            <div className="loading-indicator">
              <IoFlashOutline size={32} />
              <p>{t('loadingHistory')}</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              {/* Compact Port Status List */}
              <div className="port-status-compact">
                {combinedHistory.map(port => {
                  const currentStatus = getCurrentPortStatus(port);
                  const sessionLength = getCurrentSessionLength(port);
                  
                  // Calculate how long the port has been in current status
                  const getStatusDuration = () => {
                    if (!port.combinedIntervals || port.combinedIntervals.length === 0) return 0;
                    
                    // Get the last interval (current status)
                    const lastInterval = port.combinedIntervals[port.combinedIntervals.length - 1];
                    const durationMinutes = (lastInterval.endInterval - lastInterval.startInterval + 1) * intervalDuration;
                    return Math.round(durationMinutes);
                  };
                  
                  const statusDuration = getStatusDuration();
                  
                  return (
                    <div key={port.portNumber} className="port-status-line">
                      <div className="port-info">
                        <div 
                          className={`port-status-dot status-${
                            currentStatus === 2 ? 'available' : 
                            currentStatus === 1 ? 'in-use' : 'not-available'
                          }`}
                        ></div>
                        <div className="port-status-text">
                          <strong>Port {port.portNumber}</strong>
                          {port.chargingSpeed && (
                            <span className="charging-speed">{port.chargingSpeed}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className={`port-duration port-duration-${
                        currentStatus === 2 ? 'available' : 
                        currentStatus === 1 ? 'in-use' : 'not-available'
                      }`}>
                        <span>
                          {currentStatus === 2 && t('availableForMinutes', { minutes: statusDuration })}
                          {currentStatus === 1 && t('inUseForMinutes', { minutes: statusDuration })}
                          {currentStatus === 0 && (statusDuration >= 1440 ? t('down') : t('downForMinutes', { minutes: statusDuration }))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Collapsible 24-Hour History */}
              <div className={`detailed-history-container ${showPortHistory ? 'expanded' : ''}`}>
                <button 
                  className="section-toggle detailed-history-toggle"
                  onClick={() => setShowPortHistory(!showPortHistory)}
                >
                  <h4>{t('historyDetails')}</h4>
                  <span className={`toggle-icon ${showPortHistory ? 'expanded' : ''}`}>▼</span>
                </button>
                
                {showPortHistory && (
                  <div className="history-content">
                  <div className="history-legend">
                    <div className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: 'var(--color-success)' }}></span>
                      <span>{t('available')}</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: 'var(--color-warning)' }}></span>
                      <span>{t('inUse')}</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: 'var(--color-error)' }}></span>
                      <span>{t('notAvailable')}</span>
                    </div>
                  </div>
                  
                  {/* Desktop visualization */}
                  <div className="port-history-container">
                    <div style={{ display: 'flex' }}>
                      {/* Empty space to align with port labels */}
                      <div style={{ width: '80px' }}></div>
                      
                      {/* Time labels container aligned with status bars */}
                      <div className="time-labels-container" style={{ 
                        position: 'relative',
                        height: '24px',
                        marginBottom: 'var(--space-4)',
                        borderBottom: '1px solid var(--color-gray-200)',
                        width: '100%'
                      }}>
                        {generateTimeLabels().map((label, index) => (
                          <div 
                            key={index} 
                            className="time-label"
                            style={{ 
                              left: label.position,
                              bottom: '0'
                            }}
                          >
                            {label.time}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {combinedHistory.map(port => (
                      <div key={port.portNumber} className="port-history">
                        <div className="port-label">{t('port')} {port.portNumber}</div>
                        <div className="status-bars">
                          {port.combinedIntervals.map((interval, idx) => (
                            <div 
                              key={idx} 
                              className="status-bar" 
                              style={{ 
                                backgroundColor: getStatusColor(interval.status),
                                width: `${interval.width}%`
                              }}
                              title={`${formatTimeFromInterval(interval.startInterval)} - ${formatTimeFromInterval(interval.endInterval + 1)} | ${getStatusText(interval.status)}`}
                            ></div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Mobile horizontal scrollable visualization */}
                  <div className="mobile-scroll-container">
                    <div className="mobile-scroll-hint">
                      {t('scrollHint')}
                    </div>
                    
                    {combinedHistory.map(port => {
                      const currentStatus = getCurrentPortStatus(port);
                      const sessionLength = getCurrentSessionLength(port);
                      
                      return (
                        <div key={port.portNumber} className="mobile-port-timeline">
                          <div className="mobile-port-header">
                            <div className="mobile-port-label">{t('port')} {port.portNumber}</div>
                            <div className="mobile-current-status">
                              <div 
                                className={`mobile-status-dot mobile-status-${
                                  currentStatus === 2 ? 'available' : 
                                  currentStatus === 1 ? 'in-use' : 'not-available'
                                }`}
                              ></div>
                              <div className="mobile-status-info">
                                <span>{getStatusText(currentStatus)}</span>
                                {sessionLength > 0 && (
                                  <span className={`session-length session-length-${
                                    currentStatus === 2 ? 'available' : 
                                    currentStatus === 1 ? 'in-use' : 'not-available'
                                  }`}>{sessionLength} {t('min')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div 
                            className="mobile-scroll-timeline"
                            ref={(el) => {
                              if (el && port.combinedIntervals.length > 0) {
                                // Immediately scroll to the end when the element is rendered
                                el.scrollLeft = el.scrollWidth;
                              }
                            }}
                          >
                            <div className="mobile-status-bars-container">
                              {port.combinedIntervals.map((interval, idx) => (
                                <div 
                                  key={idx} 
                                  className={`mobile-status-bar mobile-status-${
                                    interval.status === 2 ? 'available' : 
                                    interval.status === 1 ? 'in-use' : 'not-available'
                                  }`}
                                  style={{ 
                                    width: `${(interval.width / 100) * 2880}px` // Much wider for minute-level precision
                                  }}
                                  title={`${getStatusText(interval.status)} - ${interval.width.toFixed(1)}% of timeline`}
                                ></div>
                              ))}
                            </div>
                            
                            <div className="mobile-time-labels">
                              {generateMobileTimeLabels().map((label, index) => (
                                <div key={index} className="mobile-time-label">{label}</div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Heatmap Section */}
        <div className="heatmap-section">
          <div className="intelligence-header">
            <IoFlashOutline size={24} color="var(--color-primary)" />
            <h3>{t('busyHours')}</h3>
            <div className="info-button-container">
              <button 
                className="info-button"
                onClick={() => setShowInfoTooltip(!showInfoTooltip)}
              >
                ?
              </button>
              {showInfoTooltip && (
                <div className="info-tooltip">
                  <p>{t('busyHoursDescription')}</p>
                  <p>Taller bars = More people usually charging at that time</p>
                  <p>The blue bar shows the current hour.</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="heatmap-content">
            {intelligenceData && intelligenceData.daily_patterns ? (
              <div className="heatmap-container">
                  
                  <div className="google-style-timeline">
                    
                    <div className="chart-container">
                      <div className="y-axis-labels">
                        <div className="y-label">{t('crowded')}</div>
                        <div className="y-label">{t('shortWait')}</div>
                        <div className="y-label">{t('noWait')}</div>
                      </div>
                      <div className="chart-wrapper">
                        <div className="chart-bars">
                          {Array.from({ length: 24 }, (_, i) => {
                            const hour = i;
                            // Find patterns for both the exact hour and half-hour
                            const hourPattern = intelligenceData.daily_patterns.find(p => p.time_bucket === hour);
                            const halfHourPattern = intelligenceData.daily_patterns.find(p => p.time_bucket === hour + 0.5);
                            
                            // Use the pattern with higher confidence/activity
                            let pattern = hourPattern;
                            if (halfHourPattern && (!hourPattern || halfHourPattern.confidence_score > hourPattern.confidence_score)) {
                              pattern = halfHourPattern;
                            }
                            
                            const isCurrentHour = hour === new Date().getHours();
                            
                            // Calculate height based on busyness score (0-100)
                            // Higher busyness score = taller bars
                            let heightFactor = 0.1; // Default minimum height for no data
                            
                            if (pattern && pattern.busyness_score !== undefined) {
                              // Convert busyness score (0-100) to height factor (0.1-1.0)
                              // 0 = 10% height, 100 = 100% height
                              heightFactor = 0.1 + (pattern.busyness_score / 100) * 0.9;
                            } else if (pattern) {
                              // Fallback for old data format
                              if (pattern.status === 'BUSY') {
                                heightFactor = 0.8;
                              } else if (pattern.status === 'LESS_BUSY') {
                                heightFactor = 0.5;
                              } else { // VACANT
                                heightFactor = 0.2;
                              }
                            }
                            
                            const height = Math.max(12, heightFactor * 100); // Min 12px, max 100px (within 120px container)
                            
                            return (
                              <div
                                key={hour}
                                className={`chart-bar ${isCurrentHour ? 'current-bar' : ''}`}
                                style={{
                                  height: `${height}px`,
                                  backgroundColor: isCurrentHour ? 'var(--color-primary)' : '#4db6ac'
                                }}
                                title={`${hour}:00 ${pattern ? `- ${getIntelligenceStatusText(pattern.status)}` : '- No data'}`}
                              />
                            );
                          })}
                        </div>
                        
                        <div className="time-labels-google">
                          {['6a', '9a', '12p', '3p', '6p', '9p', '12a', '3a'].map((label, index) => {
                            // Map labels to correct hours: 6a=6, 9a=9, 12p=12, 3p=15, 6p=18, 9p=21, 12a=0, 3a=3
                            const hourMap = { '6a': 6, '9a': 9, '12p': 12, '3p': 15, '6p': 18, '9p': 21, '12a': 0, '3a': 3 };
                            const hour = hourMap[label];
                            const position = (hour / 24) * 100;
                            return (
                              <div 
                                key={label} 
                                className="time-label-google"
                                style={{ left: `${position}%` }}
                              >
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            ) : (
              <p>Pattern data not available</p>
            )}
          </div>
        </div>
        
        {/* Intelligence Recommendations Section - Secondary */}
        <div className="intelligence-section">
          <div className="intelligence-header">
            <IoFlashOutline size={24} color="var(--color-primary)" />
            <h3>{t('bestTimesToCharge')}</h3>
          </div>
          
          {intelligenceLoading ? (
            <div className="loading-indicator">
              <IoFlashOutline size={32} />
              <p>Analyzing patterns...</p>
            </div>
          ) : intelligenceError ? (
            <div className="error-message">
              <p>Could not load visit recommendations</p>
            </div>
          ) : intelligenceData && intelligenceData.recommendations ? (
            <div className="recommendations-grid">
              {(() => {
                const groups = getGroupedRecommendations(intelligenceData.recommendations);
                const items = [];
                
                if (groups.noWait.length > 0) {
                  items.push(
                    <div key="no-wait" className="recommendation-tile no-wait-tile">
                      <div className="tile-header">
                        <div className="tile-icon status-available"></div>
                        <div className="tile-title">{t('noWait')}</div>
                      </div>
                      <div className="tile-content">
                        <div className="time-badges">
                          {groups.noWait.map((rec, index) => (
                            <span key={index} className="time-badge">
                              {formatTimeBucket(rec.start_bucket)} - {formatTimeBucket(rec.end_bucket)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (groups.shortWait.length > 0) {
                  items.push(
                    <div key="short-wait" className="recommendation-tile short-wait-tile">
                      <div className="tile-header">
                        <div className="tile-icon status-in-use"></div>
                        <div className="tile-title">{t('shortWait')}</div>
                      </div>
                      <div className="tile-content">
                        <div className="time-badges">
                          {groups.shortWait.map((rec, index) => (
                            <span key={index} className="time-badge">
                              {formatTimeBucket(rec.start_bucket)} - {formatTimeBucket(rec.end_bucket)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (groups.expectWait.length > 0) {
                  items.push(
                    <div key="expect-wait" className="recommendation-tile expect-wait-tile">
                      <div className="tile-header">
                        <div className="tile-icon status-not-available"></div>
                        <div className="tile-title">Expect Wait</div>
                      </div>
                      <div className="tile-content">
                        <div className="time-badges">
                          {groups.expectWait.map((rec, index) => (
                            <span key={index} className="time-badge">
                              {formatTimeBucket(rec.start_bucket)} - {formatTimeBucket(rec.end_bucket)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return items;
              })()}
            </div>
          ) : (
            <div className="no-data-message">
              <p>No pattern data available yet</p>
            </div>
          )}
        </div>
        
        <AppPromoCard stationBusy={currentAvailability?.available === 0} />

        {/* Bottom Actions - Feedback and Sponsor */}
        <div className="station-bottom-actions">
          <button className="feedback-button" onClick={() => navigate('/feedback')}>
            <IoChatbubbleEllipsesOutline size={20} />
            {t('feedbackButton')}
          </button>
          
          <a
            href="https://ko-fi.com/leefreeman"
            target="_blank"
            rel="noopener noreferrer"
            className="sponsor-button"
          >
            <IoHeartOutline size={14} />
            {t('buyMeACoffee')}
          </a>
        </div>
      </div>
      <AppPopup />
    </div>
  );
}

export default StationDetail;